var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useMemo, useState } from "react";
import { Button, MessageBanner, ModalFooter } from "@folio/stripes/components";
import { useOkapiKy } from "@folio/stripes/core";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "react-query";
import { LegalPersonLegalFormEnum } from "../../types/Api";
import { createLegalPerson } from "../../api/legalPersonService";
import { createInlineLegalPersonSchema } from "../../utils/validationUtils";
import { LegalPersonGeneralFields } from "../LegalPersonGeneralFields";
import { LocalOnlyCheckbox } from "../LocalOnlyCheckbox";
import { useAuthoritySendErrorCallout } from "../../hooks/useAuthoritySendErrorCallout";
import { buildPersonInvalidSubmitContent } from "../../utils/personInvalidSubmit";
import css from "./InlineModal.module.css";
var defaultValues = {
    legalForm: LegalPersonLegalFormEnum.LEGAL,
    name: "",
    biographicalNote: "",
    localOnly: false,
};
export var CreateLegalPersonForm = function (_a) {
    var onCreated = _a.onCreated, onCancel = _a.onCancel;
    var formatMessage = useIntl().formatMessage;
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var _b = useState(null), submitError = _b[0], setSubmitError = _b[1];
    var _c = useState(null), invalidSubmitWarning = _c[0], setInvalidSubmitWarning = _c[1];
    var notifySendError = useAuthoritySendErrorCallout().notifySendError;
    var schema = useMemo(function () { return createInlineLegalPersonSchema(formatMessage); }, [formatMessage]);
    var _d = useForm({
        resolver: zodResolver(schema),
        defaultValues: defaultValues,
        mode: "onBlur",
    }), control = _d.control, handleSubmit = _d.handleSubmit;
    var _e = useMutation(function (values) {
        return createLegalPerson(ky, __assign(__assign({}, values), { legalForm: LegalPersonLegalFormEnum.LEGAL }));
    }, {
        onSuccess: function (created) {
            queryClient.invalidateQueries(["personSearchOptions"]);
            queryClient.invalidateQueries(["legal-persons"]);
            notifySendError(created);
            onCreated(created);
        },
        onError: function () {
            setSubmitError(formatMessage({
                id: "ui-br-agency.title.contributions.form.createPerson.error",
            }));
        },
    }), mutate = _e.mutate, isLoading = _e.isLoading;
    var onSubmit = function (values) {
        setSubmitError(null);
        setInvalidSubmitWarning(null);
        mutate(values);
    };
    var onInvalid = function (formErrors) {
        setInvalidSubmitWarning(buildPersonInvalidSubmitContent(formatMessage, formErrors));
    };
    return (React.createElement(React.Fragment, null,
        submitError && (React.createElement(MessageBanner, { type: "error", className: css.errorBanner }, submitError)),
        invalidSubmitWarning && (React.createElement(MessageBanner, { type: "warning", className: css.errorBanner }, invalidSubmitWarning)),
        React.createElement("form", { noValidate: true, onSubmit: handleSubmit(onSubmit, onInvalid) },
            React.createElement(LegalPersonGeneralFields, { control: control, compact: true }),
            React.createElement(LocalOnlyCheckbox, { control: control }),
            React.createElement(ModalFooter, null,
                React.createElement(Button, { buttonStyle: "primary", onClick: handleSubmit(onSubmit, onInvalid), disabled: isLoading },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.button.save" })),
                React.createElement(Button, { buttonStyle: "default", onClick: onCancel, disabled: isLoading },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.button.cancel" }))))));
};
